import { useState, useEffect } from "react";
import axios from "axios";

const IMEI_API_BASE_URL = "https://client-api.silanga.ca/temp";
const API_KEY = process.env.REACT_APP_API_TOKEN;

/**
 * Custom hook to fetch and manage weather station data by IMEI number
 * @param {string} imei - The IMEI number of the weather station
 * @param {number} refreshKey - A key that when changed will trigger a refetch
 * @returns {Object} An object containing:
 *   - stationData: The weather data for the station
 *   - loading: Boolean indicating if data is being fetched
 *   - error: Any error that occurred during fetching
 */
export const useImeiData = (imei, refreshKey = 0) => {
  const [stationData, setStationData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Fetches data for the specified IMEI
   * @async
   */
  const fetchImeiData = async () => {
    if (!imei) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(IMEI_API_BASE_URL, {
        params: { imei },
        headers: { "x-api-key": API_KEY },
      });

      setStationData(response.data);
    } catch (err) {
      console.error("API Error:", err.response || err);
      setError("Error fetching station data by IMEI.");
      setStationData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (imei) {
      fetchImeiData();
    }
  }, [imei, refreshKey]);

  return { stationData, loading, error, fetchImeiData };
};
